import { canDeleteAnnotation, canDeleteAnnotationComment } from '../../selectors';
import type {
  Annotation,
  AnnotationAnchor,
  AnnotationComment,
  AnnotationCommentId,
  AnnotationId,
  AttachmentId,
  MockCache,
  ModelAnimationMoment,
  ModelAnnotation,
  ModelAnnotationId,
  Vec3,
} from '../../types';

import { richDocFrom, richDocToText } from '../../rich-doc';
import type { RichDoc } from '../../rich-doc';
import { annotationMentionWishes } from '../fanout/annotation-wishes';
import { deliver } from '../inbox-delivery';
import { bump, requireCache } from '../runtime';

const MAX_COORDINATE = 1e7;
const MAX_COMMENT_LENGTH = 4000;
const MAX_ANIMATION_NAME_LENGTH = 200;
const MAX_AUDIO_MS = 86_400_000;

function annotationsKey(attachmentId: AttachmentId): string {
  return `annotations:${String(attachmentId)}`;
}

function invalidAnchor(field: string): never {
  throw new Error(`Неверный якорь комментария: ${field}`);
}

function isVec3(value: unknown): value is Vec3 {
  return Array.isArray(value) && value.length === 3 && value.every((part) => typeof part === 'number' && Number.isFinite(part));
}

function isFraction(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}

function isMillisecond(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_AUDIO_MS;
}

function assertPosition(position: unknown): asserts position is Vec3 {
  if (!isVec3(position) || position.some((part) => Math.abs(part) > MAX_COORDINATE)) {
    throw new Error('Неверные координаты точки');
  }
}

function assertNormal(normal: unknown): asserts normal is Vec3 {
  if (!isVec3(normal) || Math.hypot(...normal) === 0) {
    throw new Error('Неверная нормаль точки');
  }
}

function modelMoment(value: unknown): ModelAnimationMoment | null {
  if (value === null) {
    return null;
  }
  const moment = (value ?? {}) as Partial<ModelAnimationMoment>;
  const { index, name, progress } = moment;
  if (typeof index !== 'number' || !Number.isInteger(index) || index < 0) {
    return invalidAnchor('animation.index');
  }
  if (typeof name !== 'string' || name.length > MAX_ANIMATION_NAME_LENGTH) {
    return invalidAnchor('animation.name');
  }
  if (!isFraction(progress)) {
    return invalidAnchor('animation.progress');
  }
  return { index, name, progress };
}

function modelAnchor(anchor: Record<string, unknown>): AnnotationAnchor {
  const { position, normal } = anchor;
  if (!isVec3(position) || position.some((part) => Math.abs(part) > MAX_COORDINATE)) {
    return invalidAnchor('position');
  }
  if (!isVec3(normal) || Math.hypot(...normal) === 0) {
    return invalidAnchor('normal');
  }
  return { kind: 'model', position: [...position], normal: [...normal], animation: modelMoment(anchor['animation'] ?? null) };
}

function audioAnchor(anchor: Record<string, unknown>): AnnotationAnchor {
  const { startMs, endMs } = anchor;
  if (!isMillisecond(startMs)) {
    return invalidAnchor('startMs');
  }
  if (endMs !== null && endMs !== undefined && (!isMillisecond(endMs) || endMs <= startMs)) {
    return invalidAnchor('endMs');
  }
  return { kind: 'audio', startMs, endMs: endMs ?? null };
}

function normalizeAnchor(anchor: unknown): AnnotationAnchor {
  if (typeof anchor !== 'object' || anchor === null) {
    return invalidAnchor('kind');
  }
  const fields = anchor as Record<string, unknown>;
  if (fields['kind'] === 'model') {
    return modelAnchor(fields);
  }
  if (fields['kind'] === 'audio') {
    return audioAnchor(fields);
  }
  if (fields['kind'] === 'image') {
    const { x, y } = fields;
    if (!isFraction(x)) {
      return invalidAnchor('x');
    }
    return isFraction(y) ? { kind: 'image', x, y } : invalidAnchor('y');
  }
  return invalidAnchor('kind');
}

function commentText(bodyJson: RichDoc): string {
  const text = richDocToText(bodyJson);
  if (text.length === 0 || text.length > MAX_COMMENT_LENGTH) {
    throw new Error('Комментарий пустой или слишком длинный');
  }
  return text;
}

function newComment(
  cache: MockCache,
  args: { id: AnnotationCommentId; annotationId: AnnotationId; bodyJson: RichDoc },
): AnnotationComment {
  const bodyJson = richDocFrom(args.bodyJson);
  return {
    id: args.id,
    annotationId: args.annotationId,
    authorId: cache.me,
    bodyJson,
    bodyText: commentText(bodyJson),
    createdAt: cache.nowMs(),
  };
}

function bumpWithMentions(cache: MockCache, attachmentId: AttachmentId, comment: AnnotationComment): void {
  const attachment = cache.attachments.get(attachmentId);
  const wishes = attachment === undefined ? [] : annotationMentionWishes(cache, attachment, comment);
  deliver(cache, cache.me, wishes);
  bump(annotationsKey(attachmentId), ...(wishes.length > 0 ? ['inbox'] : []));
}

function requireReadyAttachment(cache: MockCache, attachmentId: AttachmentId): void {
  const attachment = cache.attachments.get(attachmentId);
  if (attachment === undefined) {
    throw new Error('Вложение не найдено');
  }
  if (attachment.status !== 'ready') {
    throw new Error('Вложение ещё загружается');
  }
}

function insertAnnotation(args: {
  id: AnnotationId;
  attachmentId: AttachmentId;
  anchor: unknown;
  commentId: AnnotationCommentId;
  bodyJson: RichDoc;
}): Annotation {
  const cache = requireCache();
  requireReadyAttachment(cache, args.attachmentId);
  const anchor = normalizeAnchor(args.anchor);
  const comment = newComment(cache, { id: args.commentId, annotationId: args.id, bodyJson: args.bodyJson });
  const annotation: Annotation = {
    id: args.id,
    attachmentId: args.attachmentId,
    authorId: cache.me,
    anchor,
    createdAt: comment.createdAt,
    resolvedAt: null,
    resolvedBy: null,
  };
  cache.annotations.set(annotation.id, annotation);
  cache.annotationComments.set(comment.id, comment);
  bumpWithMentions(cache, annotation.attachmentId, comment);
  return annotation;
}

export function createAnnotation(args: {
  id: AnnotationId;
  attachmentId: AttachmentId;
  anchor: AnnotationAnchor;
  commentId: AnnotationCommentId;
  bodyJson: RichDoc;
}): Annotation {
  return insertAnnotation(args);
}

export function createModelAnnotation(args: {
  id: ModelAnnotationId;
  attachmentId: AttachmentId;
  position: Vec3;
  normal: Vec3;
  commentId: AnnotationCommentId;
  bodyJson: RichDoc;
}): ModelAnnotation {
  requireReadyAttachment(requireCache(), args.attachmentId);
  assertPosition(args.position);
  assertNormal(args.normal);
  const annotation = insertAnnotation({
    id: args.id,
    attachmentId: args.attachmentId,
    anchor: { kind: 'model', position: args.position, normal: args.normal, animation: null },
    commentId: args.commentId,
    bodyJson: args.bodyJson,
  });
  return {
    id: annotation.id,
    attachmentId: annotation.attachmentId,
    authorId: annotation.authorId,
    position: [...args.position],
    normal: [...args.normal],
    createdAt: annotation.createdAt,
  };
}

export function addAnnotationComment(args: {
  id: AnnotationCommentId;
  annotationId: AnnotationId;
  bodyJson: RichDoc;
}): AnnotationComment {
  const cache = requireCache();
  const annotation = cache.annotations.get(args.annotationId);
  if (annotation === undefined) {
    throw new Error('Комментарий не найден');
  }
  const comment = newComment(cache, args);
  cache.annotationComments.set(comment.id, comment);
  bumpWithMentions(cache, annotation.attachmentId, comment);
  return comment;
}

function dropAnnotation(cache: MockCache, annotation: Annotation): void {
  for (const comment of [...cache.annotationComments.values()]) {
    if (comment.annotationId === annotation.id) {
      cache.annotationComments.delete(comment.id);
    }
  }
  cache.annotations.delete(annotation.id);
}

export function deleteAnnotationComment(args: { id: AnnotationCommentId }): void {
  const cache = requireCache();
  const comment = cache.annotationComments.get(args.id);
  if (comment === undefined) {
    return;
  }
  if (!canDeleteAnnotationComment(cache, comment)) {
    throw new Error('Удалить комментарий может автор или администратор');
  }
  cache.annotationComments.delete(comment.id);
  const annotation = cache.annotations.get(comment.annotationId);
  if (annotation === undefined) {
    return;
  }
  const isLast = ![...cache.annotationComments.values()].some((other) => other.annotationId === annotation.id);
  if (isLast) {
    dropAnnotation(cache, annotation);
  }
  bump(annotationsKey(annotation.attachmentId));
}

function deleteById(id: AnnotationId): void {
  const cache = requireCache();
  const annotation = cache.annotations.get(id);
  if (annotation === undefined) {
    return;
  }
  if (!canDeleteAnnotation(cache, annotation)) {
    throw new Error('Удалить комментарий может автор или администратор');
  }
  dropAnnotation(cache, annotation);
  bump(annotationsKey(annotation.attachmentId));
}

function requireAnnotation(cache: MockCache, id: AnnotationId): Annotation {
  const annotation = cache.annotations.get(id);
  if (annotation === undefined) {
    throw new Error('Комментарий не найден');
  }
  return annotation;
}

export function resolveAnnotation(args: { id: AnnotationId }): void {
  const cache = requireCache();
  const annotation = requireAnnotation(cache, args.id);
  if (annotation.resolvedAt !== null) {
    return;
  }
  annotation.resolvedAt = cache.nowMs();
  annotation.resolvedBy = cache.me;
  bump(annotationsKey(annotation.attachmentId));
}

export function reopenAnnotation(args: { id: AnnotationId }): void {
  const cache = requireCache();
  const annotation = requireAnnotation(cache, args.id);
  if (annotation.resolvedAt === null) {
    return;
  }
  annotation.resolvedAt = null;
  annotation.resolvedBy = null;
  bump(annotationsKey(annotation.attachmentId));
}

export function deleteAnnotation(args: { id: AnnotationId }): void {
  deleteById(args.id);
}

export function deleteModelAnnotation(args: { id: ModelAnnotationId }): void {
  deleteById(args.id);
}

export function removeAnnotationsOf(cache: MockCache, attachmentId: AttachmentId): void {
  const own = [...cache.annotations.values()].filter((annotation) => annotation.attachmentId === attachmentId);
  if (own.length === 0) {
    return;
  }
  for (const annotation of own) {
    dropAnnotation(cache, annotation);
  }
  bump(annotationsKey(attachmentId));
}
